import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import API from "../config/api";
import "../css/AdminSettings.css";

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    categories: null,
    faqBonusInfissi: null,
    contacts: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("categories");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem("authToken");
    const expiry = localStorage.getItem("tokenExpiry");

    if (!token || !expiry || Date.now() > parseInt(expiry)) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("tokenExpiry");
      navigate("/login");
      return;
    }

    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(API.ENDPOINTS.SETTINGS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setSettings(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching settings:", err);
      setError("Impossibile caricare le impostazioni");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API.ENDPOINTS.SETTINGS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      const data = await response.json();

      if (data.success) {
        setHasChanges(false);
        setSuccessMessage("Modifiche salvate con successo!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage("Errore durante il salvataggio: " + (data.error || data.errors?.join(", ")));
        setTimeout(() => setErrorMessage(""), 5000);
      }
    } catch (err) {
      console.error("Save error:", err);
      setErrorMessage("Errore durante il salvataggio delle modifiche");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("tokenExpiry");
    navigate("/login");
  };

  const updateSettings = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  // Categories handlers
  const updateCategory = (categoryKey, field, value) => {
    const newCategories = { ...settings.categories };
    newCategories[categoryKey] = {
      ...newCategories[categoryKey],
      [field]: value,
    };
    updateSettings("categories", newCategories);
  };

  const updateCategoryFeature = (categoryKey, featureIndex, value) => {
    const newCategories = { ...settings.categories };
    const newFeatures = [...newCategories[categoryKey].features];
    newFeatures[featureIndex] = value;
    newCategories[categoryKey] = {
      ...newCategories[categoryKey],
      features: newFeatures,
    };
    updateSettings("categories", newCategories);
  };

  const addCategoryFeature = (categoryKey) => {
    const newCategories = { ...settings.categories };
    newCategories[categoryKey] = {
      ...newCategories[categoryKey],
      features: [...newCategories[categoryKey].features, "Nuova caratteristica"],
    };
    updateSettings("categories", newCategories);
  };

  const removeCategoryFeature = (categoryKey, featureIndex) => {
    const newCategories = { ...settings.categories };
    const newFeatures = newCategories[categoryKey].features.filter(
      (_, i) => i !== featureIndex
    );
    newCategories[categoryKey] = {
      ...newCategories[categoryKey],
      features: newFeatures,
    };
    updateSettings("categories", newCategories);
  };

  // FAQ handlers
  const updateFaq = (faqIndex, field, value) => {
    const newFaq = [...settings.faqBonusInfissi];
    newFaq[faqIndex] = {
      ...newFaq[faqIndex],
      [field]: value,
    };
    updateSettings("faqBonusInfissi", newFaq);
  };

  const addFaq = () => {
    // Generate new ID based on max existing ID + 1 to avoid collisions
    const maxId = settings.faqBonusInfissi.reduce(
      (max, faq) => Math.max(max, faq.id || 0),
      0
    );
    const newFaq = [
      ...settings.faqBonusInfissi,
      {
        id: maxId + 1,
        title: "Nuova domanda",
        description: "Risposta alla domanda",
        links: [],
      },
    ];
    updateSettings("faqBonusInfissi", newFaq);
  };

  const removeFaq = (faqIndex) => {
    const newFaq = settings.faqBonusInfissi.filter((_, i) => i !== faqIndex);
    updateSettings("faqBonusInfissi", newFaq);
  };

  const addFaqLink = (faqIndex) => {
    const newFaq = [...settings.faqBonusInfissi];
    const links = newFaq[faqIndex].links || [];
    newFaq[faqIndex] = {
      ...newFaq[faqIndex],
      links: [...links, { label: "Nuovo link", url: "https://" }],
    };
    updateSettings("faqBonusInfissi", newFaq);
  };

  const updateFaqLink = (faqIndex, linkIndex, field, value) => {
    const newFaq = [...settings.faqBonusInfissi];
    const newLinks = [...(newFaq[faqIndex].links || [])];
    newLinks[linkIndex] = {
      ...newLinks[linkIndex],
      [field]: value,
    };
    newFaq[faqIndex] = {
      ...newFaq[faqIndex],
      links: newLinks,
    };
    updateSettings("faqBonusInfissi", newFaq);
  };

  const removeFaqLink = (faqIndex, linkIndex) => {
    const newFaq = [...settings.faqBonusInfissi];
    newFaq[faqIndex] = {
      ...newFaq[faqIndex],
      links: newFaq[faqIndex].links.filter((_, i) => i !== linkIndex),
    };
    updateSettings("faqBonusInfissi", newFaq);
  };

  // Contacts handlers
  const updateContact = (field, value) => {
    updateSettings("contacts", {
      ...settings.contacts,
      [field]: value,
    });
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Impostazioni</h1>
        <div className="admin-actions">
          <Link to="/admin" className="btn btn--secondary">
            ← Gestione Offerte
          </Link>
          <button
            onClick={handleSaveChanges}
            className="btn btn--primary"
            disabled={!hasChanges || saving}
          >
            {saving ? "Salvataggio..." : "Salva Modifiche"}
          </button>
          <button onClick={handleLogout} className="btn btn--outline">
            Logout
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {errorMessage && (
        <div className="error-message-banner">{errorMessage}</div>
      )}

      <div className="settings-tabs">
        <button
          className={`tab-btn ${activeTab === "categories" ? "active" : ""}`}
          onClick={() => setActiveTab("categories")}
        >
          Categorie
        </button>
        <button
          className={`tab-btn ${activeTab === "faq" ? "active" : ""}`}
          onClick={() => setActiveTab("faq")}
        >
          FAQ Bonus
        </button>
        <button
          className={`tab-btn ${activeTab === "contacts" ? "active" : ""}`}
          onClick={() => setActiveTab("contacts")}
        >
          Contatti
        </button>
      </div>

      <div className="settings-content">
        {/* Categories Tab */}
        {activeTab === "categories" && settings.categories && (
          <div className="settings-section">
            <h2>Categorie Prodotti</h2>
            <p className="section-description">
              Modifica le informazioni delle categorie di prodotti mostrate nella pagina Prodotti.
            </p>
            {Object.entries(settings.categories).map(([key, category]) => (
              <div key={key} className="category-card">
                <h3 className="category-key">{key}</h3>
                <div className="form-group">
                  <label>Titolo</label>
                  <input
                    type="text"
                    className="form-input"
                    value={category.title}
                    onChange={(e) => updateCategory(key, "title", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Descrizione</label>
                  <textarea
                    className="form-textarea"
                    value={category.description}
                    onChange={(e) => updateCategory(key, "description", e.target.value)}
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label>Caratteristiche</label>
                  {category.features.map((feature, index) => (
                    <div key={index} className="feature-row">
                      <input
                        type="text"
                        className="form-input"
                        value={feature}
                        onChange={(e) =>
                          updateCategoryFeature(key, index, e.target.value)
                        }
                      />
                      <button
                        className="btn-remove"
                        onClick={() => removeCategoryFeature(key, index)}
                        title="Rimuovi"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn btn--small btn--secondary"
                    onClick={() => addCategoryFeature(key)}
                  >
                    + Aggiungi caratteristica
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === "faq" && settings.faqBonusInfissi && (
          <div className="settings-section">
            <h2>FAQ Bonus Infissi</h2>
            <p className="section-description">
              Modifica le domande frequenti mostrate nella pagina Agevolazioni.
            </p>
            {settings.faqBonusInfissi.map((faq, faqIndex) => (
              <div key={faq.id} className="faq-card">
                <div className="faq-header">
                  <span className="faq-number">#{faqIndex + 1}</span>
                  <button
                    className="btn-remove"
                    onClick={() => removeFaq(faqIndex)}
                    title="Rimuovi FAQ"
                  >
                    ×
                  </button>
                </div>
                <div className="form-group">
                  <label>Domanda</label>
                  <input
                    type="text"
                    className="form-input"
                    value={faq.title}
                    onChange={(e) => updateFaq(faqIndex, "title", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Risposta</label>
                  <textarea
                    className="form-textarea"
                    value={faq.description}
                    onChange={(e) =>
                      updateFaq(faqIndex, "description", e.target.value)
                    }
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label>Link (opzionali)</label>
                  {(faq.links || []).map((link, linkIndex) => (
                    <div key={linkIndex} className="link-row">
                      <input
                        type="text"
                        className="form-input form-input--half"
                        placeholder="Etichetta"
                        value={link.label}
                        onChange={(e) =>
                          updateFaqLink(faqIndex, linkIndex, "label", e.target.value)
                        }
                      />
                      <input
                        type="url"
                        className="form-input form-input--half"
                        placeholder="URL"
                        value={link.url}
                        onChange={(e) =>
                          updateFaqLink(faqIndex, linkIndex, "url", e.target.value)
                        }
                      />
                      <button
                        className="btn-remove"
                        onClick={() => removeFaqLink(faqIndex, linkIndex)}
                        title="Rimuovi link"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn btn--small btn--secondary"
                    onClick={() => addFaqLink(faqIndex)}
                  >
                    + Aggiungi link
                  </button>
                </div>
              </div>
            ))}
            <button className="btn btn--secondary" onClick={addFaq}>
              + Aggiungi FAQ
            </button>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && settings.contacts && (
          <div className="settings-section">
            <h2>Informazioni di Contatto</h2>
            <p className="section-description">
              Modifica le informazioni aziendali e di contatto mostrate nel sito.
            </p>
            <div className="contacts-grid">
              <div className="form-group">
                <label>Nome Azienda</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.contacts.COMPANY_NAME || ""}
                  onChange={(e) => updateContact("COMPANY_NAME", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>P. IVA</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.contacts.VAT_NUMBER || ""}
                  onChange={(e) => updateContact("VAT_NUMBER", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>REA</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.contacts.REA_NUMBER || ""}
                  onChange={(e) => updateContact("REA_NUMBER", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={settings.contacts.CONTACT_EMAIL || ""}
                  onChange={(e) => updateContact("CONTACT_EMAIL", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>PEC</label>
                <input
                  type="email"
                  className="form-input"
                  value={settings.contacts.PEC_EMAIL || ""}
                  onChange={(e) => updateContact("PEC_EMAIL", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Telefono</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.contacts.PHONE_DISPLAY || ""}
                  onChange={(e) => updateContact("PHONE_DISPLAY", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Facebook URL</label>
                <input
                  type="url"
                  className="form-input"
                  value={settings.contacts.FACEBOOK_URL || ""}
                  onChange={(e) => updateContact("FACEBOOK_URL", e.target.value)}
                />
              </div>
              <div className="form-group form-group--full">
                <label>Indirizzo Esposizione</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.contacts.SHOWROOM_ADDRESS || ""}
                  onChange={(e) => updateContact("SHOWROOM_ADDRESS", e.target.value)}
                />
              </div>
              <div className="form-group form-group--full">
                <label>Indirizzo Officina</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.contacts.WORKSHOP_ADDRESS || ""}
                  onChange={(e) => updateContact("WORKSHOP_ADDRESS", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Città</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.contacts.ADDRESS_CITY || ""}
                  onChange={(e) => updateContact("ADDRESS_CITY", e.target.value)}
                />
              </div>
              <div className="form-group form-group--full">
                <label>Google Maps Embed URL</label>
                <input
                  type="url"
                  className="form-input"
                  value={settings.contacts.MAP_EMBED_URL || ""}
                  onChange={(e) => updateContact("MAP_EMBED_URL", e.target.value)}
                />
              </div>
              <div className="form-group form-group--full">
                <label>Google Maps Short Link</label>
                <input
                  type="url"
                  className="form-input"
                  value={settings.contacts.MAP_SHORT_LINK || ""}
                  onChange={(e) => updateContact("MAP_SHORT_LINK", e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
